import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Accordion } from '@arcflight/tf-component-library';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import bugIcon from '../../assets/icon-card-bug.svg';
import errorIcon from '../../assets/login-error.svg';
import RadioInputButton from '../RadioInputButton/RadioInputButton';
import { checkNumberIsTwoDecimalPlaces, combineFluidUplifts } from '../../utils/utils';
import { DashboardState } from '../../models';
import { DataValue } from '../../utils/updateLocalDataObject';
import { TripUpdate } from '../../models/trips';
import {
  ContentWrapper,
  DisplayText,
  Header,
  PartsDiv,
  RowHeader,
  StyledInput,
  StyledUnit,
  TitleWrapper,
  UnitWrapper,
} from './TripUpdateAirframeSection';

interface BUGUpliftsProps {
  trip: TripUpdate;
  updateTripUpdateData: (changes: { value: DataValue; key: string }[]) => void;
}

const OuterWrapper = styled.div`
  border: solid 2px #fff;
  background-color: rgba(255, 255, 255, 0);
  padding: ${({ isMobile }): string => (isMobile ? '16px 20px' : '16px 0 20px 56px')};
`;

const LogHeader = styled.span`
  text-transform: capitalize;
  font-size: 12px;
  font-weight: 500;
  color: #242d41;
`;

const OilLogWrapper = styled.div`
  border-bottom: ${({ border }): string => (border ? '1px solid rgba(36, 45, 65, 0.05)' : 'none')};
  margin-bottom: ${({ border }): string => (border ? '20px' : '0')};
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
`;

const UnitSelectionWrapper = styled.div`
  grid-column: ${({ editable }): string => (editable ? 'span 3' : 'auto')};
`;

const ErrorWrapper = styled.div`
  font-size: '16px';
  color: '#ac1717';
  margin-top: 4px;
  img {
    margin-right: 4px;
  }
`;

const BUGUpliftsSection: React.FC<BUGUpliftsProps> = ({ trip, updateTripUpdateData }) => {
  const {
    drawer: { mode },
    aircraft: { aircraftMap },
  } = useSelector((state: DashboardState) => state);

  const { id } = useParams<{ id: string }>();

  const isMobile = window.innerWidth < 451;

  const BUGCount = aircraftMap.get(id)?.standard_fields?.back_up_generator?.count;
  const BUGRename = aircraftMap.get(id)?.standard_fields?.back_up_generator?.name_override;

  const [BUGUplifts, setBUGUplifts] = useState(null);
  const [numberOfParts, setNumberOfParts] = useState(0);
  const [errorMap, setErrorMap] = useState(new Map());

  const handleInputChange = (value: number, key: string, destinationNumber: number): void => {
    if (checkNumberIsTwoDecimalPlaces(value.toString())) {
      errorMap.delete(`${key}${destinationNumber}`);
      setErrorMap(errorMap);
    } else {
      errorMap.set(`${key}${destinationNumber}`, true);
      setErrorMap(errorMap);
    }
    const foundIndex = BUGUplifts?.findIndex((log) => log?.fluid_destination_number === destinationNumber);
    BUGUplifts[foundIndex] = { ...BUGUplifts[foundIndex], [key]: value };
    const existingFluidUplifts = trip?.fluid_uplifts_attributes || trip?.fluid_uplifts;
    const newFluidArray = combineFluidUplifts(BUGUplifts, existingFluidUplifts);
    updateTripUpdateData([{ value: newFluidArray, key: 'fluid_uplifts_attributes' }]);
  };

  const handleIDGUnitChange = (value: string, index: number): void => {
    const foundIndex = BUGUplifts?.findIndex((log) => log?.fluid_destination_number === index);
    BUGUplifts[foundIndex] = { ...BUGUplifts[foundIndex], uplift_unit: value };
    const existingFluidUplifts = trip?.fluid_uplifts_attributes || trip?.fluid_uplifts;
    const newFluidArray = combineFluidUplifts(BUGUplifts, existingFluidUplifts);
    updateTripUpdateData([{ value: newFluidArray, key: 'fluid_uplifts_attributes' }]);
  };

  const buildBUGUplifts = (): ReactElement[] => {
    const IDGArray = BUGUplifts?.map((log, i) => {
      let title = log?.fluid_destination;
      if (title === 'apu') title = 'APU';
      return (
        <OilLogWrapper
          key={`${title}${log?.fluid_destination_number}-${log?.id}`}
          border={i + 1 !== BUGUplifts?.length}
        >
          <LogHeader>{`${title} ${log?.fluid_destination_number || ''}`}</LogHeader>
          <ContentWrapper
            noBorder
            columns="1fr 1fr 1fr 1fr"
            padding={i + 1 === BUGUplifts?.length ? '16px 0 0 0' : '16px 0'}
            isMobile={isMobile}
          >
            <div>
              <Header editable={mode !== 'view'}>Initial</Header>
              <UnitWrapper>
                {mode === 'view' ? (
                  <DisplayText>{log?.initial_value || '-'}</DisplayText>
                ) : (
                  <StyledInput
                    placeholder="-"
                    width="96px"
                    value={log?.initial_value || undefined}
                    step={0.1}
                    onChange={(e): void =>
                      handleInputChange(parseFloat(e.target.value), 'initial_value', log?.fluid_destination_number)
                    }
                    type="number"
                  />
                )}
                <StyledUnit edit={mode !== 'view'}>{log?.uplift_unit}</StyledUnit>
              </UnitWrapper>
              {errorMap.get(`initial_value${log?.fluid_destination_number}`) ? (
                <ErrorWrapper>
                  <img src={errorIcon} alt="error" />
                  Value must be to 2 decimal places
                </ErrorWrapper>
              ) : null}
            </div>
            <div>
              <Header editable={mode !== 'view'}>Uplift</Header>
              <UnitWrapper>
                {mode === 'view' ? (
                  <DisplayText>{log?.uplift || '-'}</DisplayText>
                ) : (
                  <StyledInput
                    placeholder="-"
                    width="96px"
                    value={log?.uplift || undefined}
                    step={0.1}
                    onChange={(e): void =>
                      handleInputChange(parseFloat(e.target.value), 'uplift', log?.fluid_destination_number)
                    }
                    type="number"
                  />
                )}
                <StyledUnit edit={mode !== 'view'}>{log?.uplift_unit}</StyledUnit>
              </UnitWrapper>
              {errorMap.get(`uplift${log?.fluid_destination_number}`) ? (
                <ErrorWrapper>
                  <img src={errorIcon} alt="error" />
                  Value must be to 2 decimal places
                </ErrorWrapper>
              ) : null}
            </div>
            <div>
              <Header editable={mode !== 'view'}>Carried forward</Header>
              <UnitWrapper>
                {mode === 'view' ? (
                  <DisplayText>{log?.carried_forwards || '-'}</DisplayText>
                ) : (
                  <StyledInput
                    placeholder="-"
                    width="96px"
                    value={log?.carried_forwards || undefined}
                    step={0.1}
                    onChange={(e): void =>
                      handleInputChange(parseFloat(e.target.value), 'carried_forwards', log?.fluid_destination_number)
                    }
                    type="number"
                  />
                )}
                <StyledUnit edit={mode !== 'view'}>{log?.uplift_unit}</StyledUnit>
              </UnitWrapper>
              {errorMap.get(`carried_forwards${log?.fluid_destination_number}`) ? (
                <ErrorWrapper>
                  <img src={errorIcon} alt="error" />
                  Value must be to 2 decimal places
                </ErrorWrapper>
              ) : null}
            </div>
            <UnitSelectionWrapper editable={mode !== 'view'}>
              <Header editable={mode !== 'view'}>Unit</Header>
              {mode === 'view' ? (
                <DisplayText>{log?.uplift_unit}</DisplayText>
              ) : (
                <RadioWrapper>
                  <RadioInputButton
                    text="ml"
                    active={log?.uplift_unit === 'ml'}
                    marginRight="8px"
                    onClick={(): void => handleIDGUnitChange('ml', log?.fluid_destination_number)}
                  />
                  <RadioInputButton
                    text="qt"
                    active={log?.uplift_unit === 'qt'}
                    marginRight="8px"
                    onClick={(): void => handleIDGUnitChange('qt', log?.fluid_destination_number)}
                  />
                  <RadioInputButton
                    text="usg"
                    active={log?.uplift_unit === 'usg'}
                    marginRight="8px"
                    onClick={(): void => handleIDGUnitChange('usg', log?.fluid_destination_number)}
                  />
                </RadioWrapper>
              )}
            </UnitSelectionWrapper>
          </ContentWrapper>
        </OilLogWrapper>
      );
    });
    return IDGArray;
  };

  useEffect(() => {
    const createBUGUplifts = () => {
      const newArray = [];
      for (let index = 1; index <= BUGCount; index += 1) {
        newArray.push({
          fluid_destination: 'bug',
          fluid_destination_number: index,
          id: '',
          uplift: '',
          fluid_type: 'oil',
          uplift_unit: aircraftMap.get(id)?.oil_unit,
          initial_value: '',
          carried_forwards: '',
        });
      }
      return newArray;
    };
    if (trip) {
      if (trip?.fluid_uplifts_attributes?.some((uplift) => uplift?.fluid_destination === 'bug')) {
        const newLogs = trip?.fluid_uplifts_attributes?.filter((uplift) => uplift?.fluid_destination === 'bug');
        setBUGUplifts(newLogs);
        const partCount = newLogs?.length * 3;
        setNumberOfParts(partCount);
      } else if (trip?.fluid_uplifts?.some((uplift) => uplift?.fluid_destination === 'bug')) {
        const newLogs = trip?.fluid_uplifts?.filter((uplift) => uplift?.fluid_destination === 'bug') || [];
        setBUGUplifts(newLogs);
        const partCount = newLogs?.length * 3;
        setNumberOfParts(partCount);
      } else {
        const newLogs = createBUGUplifts();
        setBUGUplifts(newLogs);
        const partCount = newLogs?.length * 3;
        setNumberOfParts(partCount);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BUGCount, trip]);

  return (
    <Accordion
      dash
      isExpanded={mode === 'add'}
      headerContent={
        <RowHeader>
          <TitleWrapper>
            <img src={bugIcon} alt="oil icon" />
            <span>{BUGRename ? `${BUGRename}` : `BUG`}</span>
          </TitleWrapper>
          <PartsDiv>{`${numberOfParts} parts`}</PartsDiv>
        </RowHeader>
      }
    >
      <OuterWrapper isMobile={isMobile}>{buildBUGUplifts()}</OuterWrapper>
    </Accordion>
  );
};

export default BUGUpliftsSection;
